import React, { useState, useRef, useEffect } from "react";
import { MoreHorizontal } from "lucide-react";
import { Tip } from "@/components/ui/tooltip";

// KebabMenu — vertical-dots trigger + dropdown of actions.
// Items: [{ label, icon, description?, onClick, danger?, testid? }]
export function KebabMenu({ items, testid, size = 16, align = "right", tooltip = "More actions" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const trigger = (
    <button
      onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
      data-testid={testid || "kebab-menu-trigger"}
      aria-label="Open menu"
      className="p-1.5 rounded-sm border border-transparent hover:border-neutral-200 hover:bg-neutral-100 transition-colors text-neutral-500"
    >
      <MoreHorizontal size={size} />
    </button>
  );

  return (
    <div className="relative inline-block" ref={ref}>
      {tooltip ? <Tip label={tooltip}>{trigger}</Tip> : trigger}
      {open && (
        <div
          className={`absolute z-40 mt-1 min-w-[220px] rounded-md border border-neutral-200 bg-white shadow-md py-1 ${align === "right" ? "right-0" : "left-0"}`}
          onClick={(e) => e.stopPropagation()}
          data-testid={`${testid || "kebab-menu"}-list`}
        >
          {items.map((it, i) => (
            <button
              key={i}
              onClick={() => { setOpen(false); it.onClick(); }}
              data-testid={it.testid}
              className={`w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-neutral-100 transition-colors ${
                it.danger ? "text-red-600" : "text-neutral-800"
              }`}
            >
              {it.icon && <span className="mt-0.5 shrink-0">{it.icon}</span>}
              <span className="min-w-0 flex-1">
                <span className="block text-sm">{it.label}</span>
                {it.description && (
                  <span className="block text-[11px] text-neutral-500 leading-snug mt-0.5">{it.description}</span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
